import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/cn';

export interface AccountSwitcherAccount {
  id: string;
  name: string;
}

export interface AccountSwitcherProps {
  /** Accounts the user can switch to. Empty until the API resolves. */
  accounts: AccountSwitcherAccount[];
  /** Currently active account id, or `''` if not yet resolved. */
  currentAccountId: string;
  /**
   * Switch handler. Receives the new account id; the caller is expected to
   * (a) update the platform account store and (b) refresh the access token
   * with the new `account_id` parameter.
   */
  onSwitch: (accountId: string) => void | Promise<void>;
  /** Show the loading placeholder while the accounts list is being fetched. */
  isLoading?: boolean;
  /** Override the dropdown trigger label when no account is matched. */
  emptyLabel?: string;
  className?: string;
}

/**
 * Dropdown trigger + menu listing accounts. Pure presentational — takes
 * props, calls back. The full sidebar wiring (Auth0 token refresh, account
 * store sync) lives in `apps/<app>/src/layout/AppSidebar.tsx`.
 *
 * Lifted from My.Tms.Old/components/TenantSwitcher.tsx but decoupled from
 * the old TenantContext so Depot/Expenses can reuse it as-is. Renamed
 * tenant→account to match the BE / Auth0 vocabulary.
 */
export function AccountSwitcher({
  accounts,
  currentAccountId,
  onSwitch,
  isLoading = false,
  emptyLabel = 'Loading…',
  className,
}: AccountSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentName =
    accounts.find((a) => a.id === currentAccountId)?.name ?? (isLoading ? 'Loading…' : emptyLabel);

  // Close on outside click.
  useEffect(() => {
    if (!isOpen) return;
    function onMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isOpen]);

  const handleSelect = async (accountId: string) => {
    setIsOpen(false);
    if (accountId === currentAccountId) return;
    await onSwitch(accountId);
  };

  const disabled = isLoading || accounts.length === 0;

  return (
    <div className={cn('relative', className)} ref={ref}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={cn(
          'flex w-full items-center justify-between rounded-md text-left text-sm text-slate-400 transition-colors',
          !disabled && 'hover:text-white',
          disabled && 'cursor-default opacity-70',
        )}
      >
        <span className="truncate">{currentName}</span>
        <ChevronDown
          className={cn(
            'ml-2 h-4 w-4 flex-shrink-0 text-slate-400 transition-transform',
            isOpen && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>

      {isOpen && accounts.length > 0 && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 min-w-[12rem] overflow-hidden rounded-md border border-slate-700 bg-slate-800 shadow-lg"
        >
          <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Switch account
          </div>
          {accounts.map((account) => {
            const active = account.id === currentAccountId;
            return (
              <button
                key={account.id}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => handleSelect(account.id)}
                className={cn(
                  'flex w-full items-center justify-between px-3 py-2 text-sm transition-colors',
                  active
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white',
                )}
              >
                <span className="truncate">{account.name}</span>
                {active && <Check className="ml-2 h-4 w-4 flex-shrink-0" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
